import { Container, Group, Stack, TextInput } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { IconSearch } from "@tabler/icons-react"
import type { CountryIso3166 } from "@tfg/db"
import {
  CountrySelector,
  DEFAULT_PAGE_SIZE,
  ExportButton,
  ExportType,
  InfoBox,
  Select,
} from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  orderByValidationType,
  supplierMonitoringFiltersValidationType,
  SupplierTypeValidationType,
} from "@tfg/types/schemas/organization/schemas"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { i18n } from "~/i18n.ts"
import { SupplierMonitoring } from "~/pages/Brand/Suppliers/List/SupplierMonitoring.tsx"
import {
  useListSuppliersCountriesQuery,
  useListSuppliersQuery,
} from "~/pages/Brand/Suppliers/List/supplierHooks.ts"
import { SupplierSort } from "~/pages/Brand/Suppliers/SupplierSort"
import { useDownloadSuppliersCSVMutation } from "~/pages/Brand/Suppliers/hooks.ts"
import { useListSubSuppliersQuery } from "~/pages/SupplierPortal/SubSupplierMonitoring/hooks/useListSubSuppliersQuery.ts"
import { COUNTRIES } from "~/utils/countries.ts"

export const SUPPLIER_DIRECT_OPTIONS = [
  { label: i18n.t("suppliers:filter.all"), value: "all" },
  { label: i18n.t("suppliers:filter.direct"), value: "direct" },
  {
    label: i18n.t("suppliers:filter.nonConfidential"),
    value: "indirect",
  },
  {
    label: i18n.t("suppliers:filter.indirectConfidential"),
    value: "indirectConfidential",
  },
]

const hooks = {
  supplier: {
    getList: useListSuppliersQuery,
    countries: useListSuppliersCountriesQuery,
  },
  subSupplier: {
    getList: useListSubSuppliersQuery,
    countries: () => ({ data: undefined }),
  },
}

type SupplierMonitoringWrapperProps = {
  type: "supplier" | "subSupplier"
}

export const SupplierMonitoringWrapper = ({
  type,
}: SupplierMonitoringWrapperProps) => {
  const { t } = useTranslation(["suppliers", "common"])
  const [sort, setSort] = useState<orderByValidationType>({})
  const [pagination, setPagination] = useState<number>(1)
  const fetchDataOptions = {
    pageIndex: pagination - 1,
    pageSize: DEFAULT_PAGE_SIZE,
  }
  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [debouncedSupplier] = useDebouncedValue<string>(searchSupplier, 600)

  const suppliersCountriesQuery = hooks[type].countries()
  const suppliersCountries = suppliersCountriesQuery.data ?? undefined

  const [filtersValue, setFiltersValue] =
    useState<supplierMonitoringFiltersValidationType>({
      supplierType: undefined,
      country: undefined,
    })

  const suppliersQuery = hooks[type].getList({
    fetchDataOptions,
    searchSupplier: debouncedSupplier,
    filters: filtersValue,
    orderBy: sort,
  })
  useEffect(() => {
    setPagination(1)
  }, [debouncedSupplier, filtersValue])

  const { mutate: downloadMutation, isPending: isSuppliersCsvDownloading } =
    useDownloadSuppliersCSVMutation({
      searchSupplier,
      filters: filtersValue,
      orderBy: sort,
    })
  return (
    <Stack>
      <Group justify="space-between">
        <Group align="flex-end" gap="xs">
          <TextInput
            value={searchSupplier}
            autoFocus
            onChange={(e) => setSearchSupplier(e.target.value)}
            w={180}
            leftSection={<IconSearch size={16} />}
            placeholder={t("list.searchPlaceholder")}
          />
          <SupplierSort sort={sort} setSort={setSort} />
          <Select
            data={SUPPLIER_DIRECT_OPTIONS}
            placeholder={t("list.supplierTypePlaceholder")}
            clearable
            value={filtersValue.supplierType}
            onChange={(value) => {
              setFiltersValue({
                ...filtersValue,
                supplierType:
                  (value as SupplierTypeValidationType) ?? undefined,
              })
            }}
          />
          <CountrySelector
            value={filtersValue.country ?? null}
            countriesMapper={COUNTRIES}
            onChange={(value) =>
              setFiltersValue({
                ...filtersValue,
                country: (value as CountryIso3166) ?? undefined,
              })
            }
            selectCountries={suppliersCountries}
            searchable
            clearable
            placeholder={t("filter.country")}
          />
        </Group>
        {type === "supplier" && (
          <ExportButton<void>
            label={t("common:actionButtons.export")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: downloadMutation,
                isLoading: isSuppliersCsvDownloading,
              },
            ]}
          />
        )}
      </Group>
      <DataFetchWrapper query={suppliersQuery}>
        {({ data }) =>
          data.suppliers.length > 0 ? (
            <SupplierMonitoring
              suppliers={data.suppliers}
              count={data.count}
              setPagination={setPagination}
              pagination={pagination}
              searchSupplier={searchSupplier}
              setSearchSupplier={setSearchSupplier}
              setFiltersValue={setFiltersValue}
              filtersValue={filtersValue}
              type={type}
            />
          ) : (
            <Container mt="10%">
              <InfoBox message={t("list.noSuppliers")} />
            </Container>
          )
        }
      </DataFetchWrapper>
    </Stack>
  )
}
